import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/utils/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { investorEncompassTemplateService } from '@/services/investorEncompassTemplateService';
import { getGoodbyeLetterSettings } from '@/services/goodbyeLetterSettingsService';
import { bankerReconService } from '@/services/bankerReconService';
import {
  downloadAuthorizedGoodbyeLetterPdf,
  getAuthorizedGoodbyeLetterData
} from '@/services/goodbyeLetterService';
import { resolveGoodbyeLetterErrorMessage } from '@/components/servicing/goodbyeLetterErrors';
import {
  type GoodbyeLetterData,
  GoodbyeLetterPrereqError,
  type GoodbyeLetterSettings
} from '@/types/goodbye-letter';
import type { BankerRecon } from '@/types/banker-recon';
import type {
  EncompassTemplateContact,
  InvestorEncompassTemplate
} from '@/types/investor-encompass';
import { suggestServicerTemplate } from '../../../supabase/functions/_shared/goodbye-letter-servicer-suggestion';
import {
  resolveLoanProduct,
  selectTemplateForProduct
} from '../../../supabase/functions/_shared/investor-template-resolution';
import { resolveGoodbyePaymentWebsite } from '@/utils/goodbye-letter/website';

export type ServicerSelection = Pick<
  BankerRecon,
  | 'servicing_template_id'
  | 'servicing_template_selected_by'
  | 'servicing_template_selected_at'
  | 'servicing_template_selection_source'
  | 'servicing_template_evidence'
>;
interface Props {
  selectedLoan: {
    encompass_loan_id: string;
    subject_property_state?: string | null;
    trans_details_loan_type?: string | null;
    rate_lock_loan_program?: string | null;
    cx_doc_type?: string | null;
  };
  recon: ServicerSelection & { id?: string | null; investor_id?: string | null; status: string };
  canEdit: boolean;
  setIsDirty: (dirty: boolean) => void;
  setSelection: (selection: ServicerSelection) => void;
  servicingLoanId?: string | null;
  beforeCheck?: () => Promise<boolean>;
}
interface Candidate {
  template: InvestorEncompassTemplate;
  settings: GoodbyeLetterSettings | null;
}
const address = (contact?: EncompassTemplateContact) =>
  [
    contact?.addressStreet1,
    contact?.addressStreet2,
    [contact?.city, [contact?.state, contact?.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
  ].filter(Boolean).join('\n');
const completeAddress = (c?: EncompassTemplateContact) =>
  [c?.addressStreet1, c?.city, c?.state, c?.zip].every((v) => !!v?.trim());

export function ServicingTab(
  { selectedLoan, recon, canEdit, setIsDirty, setSelection, servicingLoanId, beforeCheck }: Props
) {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [investor, setInvestor] = useState<
    { investor_name: string; servicer_selection_mode: string } | null
  >(null);
  const [paText, setPaText] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [readiness, setReadiness] = useState<GoodbyeLetterData | null>(null);
  const [readinessError, setReadinessError] = useState<{ title: string; detail: string } | null>(
    null
  );
  const readinessRequestId = useRef(0);
  const invalidateReadiness = useCallback(() => {
    readinessRequestId.current += 1;
    setReadiness(null);
    setReadinessError(null);
    setChecking(false);
  }, []);
  useEffect(() => {
    invalidateReadiness();
  }, [recon.servicing_template_id, invalidateReadiness]);
  useEffect(() => {
    invalidateReadiness();
  }, [recon.investor_id, selectedLoan.encompass_loan_id, invalidateReadiness]);
  useEffect(() => {
    let cancelled = false;
    setCandidates([]);
    setInvestor(null);
    setPaText(null);
    setState(null);
    setError(null);
    if (!recon.investor_id) {
      return;
    }
    setLoading(true);
    void (async () => {
      try {
        const [investorResult, templates, documents, loanResult] = await Promise.all([
          supabase.from('investors').select('investor_name, servicer_selection_mode').eq(
            'id',
            recon.investor_id!
          ).maybeSingle(),
          investorEncompassTemplateService.getTemplatesByInvestorId(recon.investor_id!),
          bankerReconService.getReconDocumentTexts({
            encompassLoanId: selectedLoan.encompass_loan_id,
            reconId: recon.id
          }),
          supabase.from('encompass_loans').select('subject_property_state').eq(
            'encompass_loan_id',
            selectedLoan.encompass_loan_id
          ).maybeSingle()
        ]);
        if (investorResult.error) {
          throw investorResult.error;
        }
        if (loanResult.error) {
          throw loanResult.error;
        }
        const rows = await Promise.all(
          templates.map(async (template) => ({
            template,
            settings: await getGoodbyeLetterSettings(template.id)
          }))
        );
        if (cancelled) {
          return;
        }
        setCandidates(rows);
        setInvestor(investorResult.data);
        setPaText(
          documents.find((d) => d.documentType === 'purchase_advice')?.extractedText ?? null
        );
        setState(loanResult.data?.subject_property_state ?? null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load servicer information');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recon.investor_id, recon.id, selectedLoan.encompass_loan_id]);
  const perLoan = investor?.servicer_selection_mode === 'per_loan';
  const suggestion = useMemo(() =>
    suggestServicerTemplate({
      candidates: candidates.map((c) => ({
        templateId: c.template.id,
        templateName: c.template.templateName,
        paServicerCodes: c.settings?.paServicerCodes ?? [],
        propertyStates: c.settings?.propertyStates ?? []
      })),
      purchaseAdviceText: paText,
      propertyState: state
    }), [candidates, paText, state]);
  useEffect(() => {
    if (
      !perLoan || !canEdit || recon.servicing_template_id || !suggestion.templateId
    ) {
      return;
    }
    invalidateReadiness();
    setSelection({
      servicing_template_id: suggestion.templateId,
      servicing_template_selected_by: null,
      servicing_template_selected_at: new Date().toISOString(),
      servicing_template_selection_source: suggestion.source,
      servicing_template_evidence: suggestion.evidence
    });
    setIsDirty(true);
  }, [
    perLoan,
    canEdit,
    recon.servicing_template_id,
    suggestion,
    invalidateReadiness,
    setSelection,
    setIsDirty
  ]);
  const selectManually = (templateId: string) => {
    if (!canEdit || !user?.id) {
      return;
    }
    invalidateReadiness();
    setSelection({
      servicing_template_id: templateId,
      servicing_template_selected_by: user.id,
      servicing_template_selected_at: new Date().toISOString(),
      servicing_template_selection_source: 'manual',
      servicing_template_evidence: suggestion.evidence
    });
    setIsDirty(true);
  };
  const product = resolveLoanProduct({
    rateLockLoanProgram: selectedLoan.rate_lock_loan_program,
    transDetailsLoanType: selectedLoan.trans_details_loan_type,
    cxDocType: selectedLoan.cx_doc_type
  });
  const productSelection = selectTemplateForProduct(
    candidates.map((c) => c.template),
    candidates.flatMap((c) =>
      c.template.assignments?.length
        ? c.template.assignments.map((a) => ({ template_id: c.template.id, product: a.product }))
        : (c.template.applicableProducts ?? []).map((p) => ({
          template_id: c.template.id,
          product: p
        }))
    ),
    product.product
  );
  const visible = perLoan
    ? candidates
    : candidates.filter((c) => c.template.id === productSelection.template?.id);
  const check = async (preview: boolean) => {
    const requestId = ++readinessRequestId.current;
    if (!servicingLoanId) {
      return;
    }
    setChecking(true);
    setReadiness(null);
    setReadinessError(null);
    try {
      if (beforeCheck && canEdit && !await beforeCheck()) {
        if (requestId === readinessRequestId.current) {
          setChecking(false);
          readinessRequestId.current += 1;
        }
        return;
      }
      if (requestId !== readinessRequestId.current) {
        return;
      }
      if (preview) {
        await downloadAuthorizedGoodbyeLetterPdf(servicingLoanId, { draft: true });
      } else {
        const data = await getAuthorizedGoodbyeLetterData(servicingLoanId, { draft: true });
        if (requestId === readinessRequestId.current) {
          setReadiness(data);
        }
      }
    } catch (err) {
      if (requestId === readinessRequestId.current) {
        setReadinessError(
          err instanceof GoodbyeLetterPrereqError ? resolveGoodbyeLetterErrorMessage(err) : {
            title: 'Letter check failed',
            detail: err instanceof Error ? err.message : 'Please try again.'
          }
        );
      }
    } finally {
      if (requestId === readinessRequestId.current) {
        setChecking(false);
      }
    }
  };
  const cards = visible.map(({ template, settings }) => {
    const payment = template.contacts?.find((c) =>
      c.contactType === (settings?.paymentContactRole ?? 'Payment')
    );
    const customer = template.contacts?.find((c) =>
      c.contactType === (settings?.customerServiceContactRole ?? 'CustomerService')
    );
    const notices = template.contacts?.find((c) =>
      c.contactType === (settings?.correspondenceContactRole ?? 'Correspondence')
    );
    const website = resolveGoodbyePaymentWebsite(settings, payment?.website);
    const missing = [
      !settings && 'Goodbye Letter Settings',
      !completeAddress(payment) && 'payment address',
      !payment?.phone?.trim() && 'payment phone',
      !(settings?.customerServicePhoneOverride?.trim() || customer?.phone?.trim()) &&
      'customer service phone',
      !completeAddress(notices) && 'notices address',
      settings?.onlinePaymentsEnabled && !website.url && 'website',
      template.syncStatus !== 'synced' && 'template sync'
    ].filter(Boolean);
    return (
      <Card key={template.id} className='min-w-0'>
        <CardHeader className='space-y-2 pb-3'>
          <div className='flex items-start gap-3'>
            {perLoan && (
              <RadioGroupItem
                id={`servicer-${template.id}`}
                value={template.id}
                aria-label={template.templateName}
                disabled={!canEdit || checking}
                onClick={() => {
                  if (recon.servicing_template_id === template.id) {
                    selectManually(template.id);
                  }
                }}
              />
            )}
            <h3 className='min-w-0 break-words text-sm font-semibold'>
              <label htmlFor={`servicer-${template.id}`}>{template.templateName}</label>
            </h3>
            {suggestion.templateId === template.id && perLoan && (
              <Badge variant='outline'>Suggested</Badge>
            )}
          </div>
          {suggestion.templateId === template.id && perLoan && (
            <p className='break-words text-xs text-muted-foreground'>
              {suggestion.evidence?.matchedCode || suggestion.evidence?.state ||
                'Only linked template'}
              {suggestion.evidence?.snippet ? ` · ${suggestion.evidence.snippet}` : ''}
            </p>
          )}
        </CardHeader>
        <CardContent className='space-y-3 text-sm'>
          <div>
            <p className='font-medium'>{payment?.investorName}</p>
            <p>{payment?.contactName?.trim() || payment?.attentionTo}</p>
            <p className='whitespace-pre-line'>{address(payment)}</p>
          </div>
          <p>Payment phone: {payment?.phone || '—'}</p>
          <p className='break-all'>Website: {website.url || '—'}</p>
          <p>
            Customer service phone:{' '}
            {settings?.customerServicePhoneOverride || customer?.phone || '—'}
          </p>
          <div>
            <p className='font-medium'>Notices address</p>
            <p>{notices?.investorName}</p>
            <p>{settings?.qwrAttention || notices?.contactName || notices?.attentionTo}</p>
            <p className='whitespace-pre-line'>{address(notices)}</p>
          </div>
          {missing.length > 0 && <p className='text-destructive'>Missing: {missing.join(', ')}</p>}
        </CardContent>
      </Card>
    );
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Servicer for this loan</CardTitle>
        <p className='text-sm text-muted-foreground'>{investor?.investor_name}</p>
      </CardHeader>
      <CardContent className='space-y-4'>
        {!recon.investor_id
          ? <p>Select the investor on the Investor Purchase Advice tab first.</p>
          : loading
          ? <p role='status'>Loading servicers…</p>
          : error
          ? <p role='alert'>{error}</p>
          : (
            <>
              <div className='flex flex-wrap gap-x-6 gap-y-2 text-sm'>
                <p>
                  Purchase advice says:{' '}
                  {suggestion.evidence?.matchedCode || 'no servicer code found'}
                </p>
                <p>Property state: {state || '—'}</p>
              </div>
              {perLoan
                ? (
                  <RadioGroup
                    aria-label='Servicer for this loan'
                    value={recon.servicing_template_id ?? ''}
                    onValueChange={selectManually}
                    className='grid grid-cols-1 gap-3 lg:grid-cols-2'
                  >
                    {cards}
                  </RadioGroup>
                )
                : (
                  <div className='grid grid-cols-1 gap-3 lg:grid-cols-2'>
                    {cards.length ? cards : <p>{productSelection.reason}</p>}
                  </div>
                )}
              {servicingLoanId
                ? (
                  <div className='flex flex-wrap gap-2'>
                    <Button
                      type='button'
                      variant='outline'
                      disabled={checking}
                      onClick={() => void check(false)}
                    >
                      Check letter readiness
                    </Button>
                    <Button
                      type='button'
                      variant='outline'
                      disabled={checking}
                      onClick={() => void check(true)}
                    >
                      Preview draft letter
                    </Button>
                  </div>
                )
                : (
                  <p className='text-sm text-muted-foreground'>
                    Letter readiness and preview become available once the servicing record exists.
                  </p>
                )}
              {checking && <p role='status'>Checking letter…</p>}
              {readiness && (
                <div role='status' className='space-y-1 text-sm'>
                  <p className='font-medium text-success'>Ready to generate</p>
                  <p>{readiness.newServicerName}</p>
                  <p>Next payment: {readiness.nextPayment?.dueDate || '—'}</p>
                  <p>Servicing transfer: {readiness.servicingTransferDate}</p>
                  <p>Payments start: {readiness.servicerPaymentsStartDate}</p>
                </div>
              )}
              {readinessError && (
                <div role='alert' className='text-sm text-destructive'>
                  <p className='font-medium'>{readinessError.title}</p>
                  <p>{readinessError.detail}</p>
                </div>
              )}
            </>
          )}
      </CardContent>
    </Card>
  );
}
